import React from "react";

/**
 * <ListTable />
 *
 * Shared, opinionated single-panel table used across the City Staff and
 * Platform Admin list surfaces. Eliminates duplicated CSS for:
 *   • bordered single-panel container
 *   • sticky-style grid header strip
 *   • loading shimmer skeleton
 *   • empty-state card
 *   • hairline row dividers
 *
 * The component does NOT prescribe row content — pages still own their
 * row renderer for layout fidelity. This struts the chrome only.
 *
 * Props
 * ─────
 *   columns        Array<{ label: string, align?: 'left'|'right'|'center' }>
 *   gridTemplate   string  CSS grid-template-columns value
 *   gap            number  px gap between columns (default 18)
 *   items          Array<any> | undefined  undefined = loading
 *   renderRow      (item, index, isLast) => ReactNode
 *   emptyTitle     string  shown when items.length === 0
 *   emptyBody      ReactNode  optional secondary copy on empty state
 *   loadingRows    number  shimmer rows to render while loading (default 3)
 *   loadingHeight  number  shimmer row height in px (default 62)
 *   testId         string  data-testid root prefix (e.g. "admin-users")
 */
export default function ListTable({
  columns,
  gridTemplate,
  gap = 18,
  items,
  renderRow,
  emptyTitle = "Nothing matches this filter.",
  emptyBody,
  loadingRows = 3,
  loadingHeight = 62,
  testId,
}) {
  if (items == null) {
    return (
      <div
        className="mt-5"
        style={{
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          overflow: "hidden",
        }}
        data-testid={testId ? `${testId}-loading` : undefined}
      >
        {Array.from({ length: loadingRows }).map((_, i) => (
          <div
            key={i}
            className="shimmer"
            style={{
              height: loadingHeight,
              borderBottom:
                i < loadingRows - 1 ? "1px solid #E8E7EE" : "none",
              borderRadius: 0,
            }}
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="wn-card mt-5"
        style={{ padding: 24 }}
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 18,
            color: "#1B1F4B",
          }}
        >
          {emptyTitle}
        </div>
        {emptyBody && (
          <div style={{ color: "#6C6B85", fontSize: 13, marginTop: 4 }}>
            {emptyBody}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="mt-5"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        overflow: "hidden",
        background: "#FBFAFD",
      }}
      data-testid={testId ? `${testId}-list` : undefined}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap,
          alignItems: "center",
          padding: "10px 22px",
          background: "#F3F2F6",
          borderBottom: "1px solid #D9D8E4",
        }}
      >
        {columns.map((c, i) => (
          <span
            key={i}
            className="wn-label"
            style={{
              color: "#6C6B85",
              letterSpacing: "0.08em",
              fontSize: 10.5,
              textAlign: c.align || "left",
            }}
          >
            {c.label}
          </span>
        ))}
      </div>
      {items.map((item, i) => renderRow(item, i, i === items.length - 1))}
    </div>
  );
}

/**
 * <ListRow />
 *
 * Companion helper that handles the row grid + hover + hairline divider so
 * pages don't have to repeat the boilerplate. Accepts either an `as`
 * component (e.g. Link) or falls through to a div with onClick.
 */
export function ListRow({
  as: Component = "div",
  gridTemplate,
  gap = 18,
  isLast,
  children,
  style: extraStyle,
  ...rest
}) {
  const base = {
    display: "grid",
    gridTemplateColumns: gridTemplate,
    gap,
    alignItems: "center",
    padding: "14px 22px",
    textDecoration: "none",
    borderBottom: isLast ? "none" : "1px solid #E8E7EE",
    transition: "background-color 140ms ease",
    ...extraStyle,
  };
  return (
    <Component
      {...rest}
      style={base}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#F3F2F6";
        rest.onMouseEnter && rest.onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        rest.onMouseLeave && rest.onMouseLeave(e);
      }}
    >
      {children}
    </Component>
  );
}
